import React from 'react';
import { Link } from 'react-router-dom';

export interface TabItem<K extends string = string> {
  key: K;
  label: string;
  /** Remix icon class, rendered before the label. */
  icon?: string;
  /** Count badge; hidden when 0 or undefined. */
  badge?: number;
  /** When set the tab is a router link; otherwise a button firing onChange. */
  to?: string;
}

export interface TabsProps<K extends string = string> {
  items: TabItem<K>[];
  active: K;
  onChange?: (key: K) => void;
  /** Describes the row for screen readers, e.g. "Settings sections". */
  ariaLabel?: string;
  className?: string;
}

const tabClass = (isActive: boolean) =>
  `-mb-px flex items-center gap-2 border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
    isActive
      ? 'border-[#5e2aac] text-[#5e2aac]'
      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
  }`;

/**
 * The brand-underlined tab row every list screen draws (rent activity,
 * handovers, settings, tracking): one implementation instead of a copy per
 * page. Link tabs for sub-routes, button tabs for in-page state.
 */
export function Tabs<K extends string = string>({
  items,
  active,
  onChange,
  ariaLabel,
  className = '',
}: TabsProps<K>) {
  return (
    <div className={`border-b border-gray-200 mb-4 ${className}`.trim()}>
      <nav className="flex gap-6" aria-label={ariaLabel}>
        {items.map((tab) => {
          const isActive = tab.key === active;
          const content = (
            <>
              {tab.icon && <i className={tab.icon}></i>}
              <span>{tab.label}</span>
              {typeof tab.badge === 'number' && tab.badge > 0 && (
                <span
                  className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-bold leading-none ${
                    isActive
                      ? 'bg-[#5e2aac] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </>
          );
          return tab.to ? (
            <Link
              key={tab.key}
              to={tab.to}
              aria-current={isActive ? 'page' : undefined}
              className={tabClass(isActive)}
            >
              {content}
            </Link>
          ) : (
            <button
              key={tab.key}
              type="button"
              onClick={() => onChange?.(tab.key)}
              aria-current={isActive ? 'page' : undefined}
              className={tabClass(isActive)}
            >
              {content}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default Tabs;
